import { useCallback, useEffect, useRef } from 'react';

type Source = 'Dashboard' | 'Legacy Splat' | 'Container' | 'Auth' | 'Chat';

// Messages
type MessageCreateLead = {
  type: 'COMMAND:CREATE_LEAD';
  source: Source;
};
type MessageSetRetailStoreLocation = {
  type: 'COMMAND:SET_RETAIL_STORE_LOCATION';
  source: Source;
  payload: {
    retailStoreLocation: string;
    retailStoreName: string;
    channelType: string;
  };
};
type MessageCommandUrlChange = {
  type: 'COMMAND:CHANGE_URL';
  source: Source;
  payload: {
    url: string;
  };
};
type EventReady = {
  type: `EVENT:READY`;
  source: Source;
};
type EventBootstrap = {
  type: `EVENT:BOOTSTRAP`;
  source: Source;
};
type MessageQueryAuthToken = {
  type: 'QUERY:AUTH_TOKEN';
  source: Source;
};
type MessageOnAuthToken = {
  type: 'EVENT:AUTH_TOKEN';
  source: Source;
  payload: {
    token: string;
    tokenType: string;
  };
};
type MessageQuerySplatInMobile = {
  type: 'QUERY:SPLAT_IN_MOBILE_APP';
  source: Source;
};
type MessageEventSplatInMobileOpenExternalWindow = {
  type: 'EVENT:MOBILE_APP_EXTERNAL_LINK_CLICKED';
  source: Source;
  payload: {
    url: string;
  };
};

const EVENT_SPLAT_IN_MOBILE_APP = 'EVENT:SPLAT_IN_MOBILE_APP';
const QUERY_SPLAT_IN_MOBILE_APP = 'QUERY:SPLAT_IN_MOBILE_APP';
const EVENT_MOBILE_APP_EXTERNAL_LINK_CLICKED =
  'EVENT:MOBILE_APP_EXTERNAL_LINK_CLICKED';

type MessageEventOnSplatInMobile = {
  type: 'EVENT:SPLAT_IN_MOBILE_APP';
  source: Source;
  payload: {
    isInOneMobileApp: boolean;
  };
};

type MessageChatClosingChatConversation = {
  type: 'CHAT:CLOSING_CHAT_CONVERSATION';
  source: Source;
};

type MessageGetParentRetailStoreData = {
  type: 'EVENT:GET_PARENT_RETAIL_STORE_DATA';
  source: Source;
  payload: {
    channelType: string;
    retailStoreLocation: string;
    retailStoreName: string;
  };
};

type Message =
  | MessageCreateLead
  | MessageSetRetailStoreLocation
  | MessageCommandUrlChange
  | MessageQueryAuthToken
  | MessageOnAuthToken
  | MessageQuerySplatInMobile
  | MessageEventOnSplatInMobile
  | MessageEventSplatInMobileOpenExternalWindow
  | EventReady
  | EventBootstrap
  | MessageChatClosingChatConversation
  | MessageGetParentRetailStoreData;

type Subscriber = {
  id: string;
  action: (action: Message) => void;
};

let subscribers: Subscriber[] = [];

const subscribe = (action: Subscriber['action']) => {
  const id = Math.random().toString(36);
  subscribers.push({ id, action });
  return () => {
    subscribers = subscribers.filter((subscriber) => subscriber.id !== id);
  };
};

const useSubscribe = (action: Subscriber['action']) => {
  const actionRef = useRef(action);
  actionRef.current = action;

  const stableAction = useCallback(
    (message: Message) => actionRef.current(message),
    [],
  );

  useEffect(() => subscribe(stableAction), [stableAction]);
};

window.addEventListener<Message | any>('message', (event) => {
  if (event.origin === window.origin) {
    return;
  }
  const message: Message = event.data;
  [...subscribers].forEach((subscriber) => subscriber.action(message));
});

const publishMessage = (message: Message) => {
  const event = message;
  window.parent.postMessage(event, '*');
};

const publish = {
  queryAuthToken: () => {
    const message: MessageQueryAuthToken = {
      source: 'Dashboard',
      type: 'QUERY:AUTH_TOKEN',
    };
    publishMessage(message);
  },
  createLead: () => {
    const message: MessageCreateLead = {
      source: 'Dashboard',
      type: 'COMMAND:CREATE_LEAD',
    };
    publishMessage(message);
  },
  openTerros: () => {
    const message: MessageCommandUrlChange = {
      source: 'Dashboard',
      type: 'COMMAND:CHANGE_URL',
      payload: {
        url: 'https://app.terros.com',
      },
    };
    publishMessage(message);
  },
};

export type { Message };
export {
  useSubscribe,
  publish,
  publishMessage,
  QUERY_SPLAT_IN_MOBILE_APP,
  EVENT_SPLAT_IN_MOBILE_APP,
  EVENT_MOBILE_APP_EXTERNAL_LINK_CLICKED,
};
